/**
 * Import Wallet Form
 *
 * Form to import an existing wallet via mnemonic, private key, or backup file
 */

import { useState } from "react";
import {
  ArrowLeft,
  Key,
  FileText,
  Loader2,
  Fingerprint,
  Upload,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  useBrowserWallet,
  useBrowserWalletStore,
} from "@nullaone/wallet-react";

type ImportMethod = "mnemonic" | "privateKey" | "backup";

interface ImportFormState {
  method: ImportMethod;
  value: string;
  name: string;
  isImporting: boolean;
}

export function ImportWalletForm() {
  const { t } = useTranslation();
  const { importWallet, restoreFromBackup, connecting, closeModal } =
    useBrowserWallet();
  const { setModalStep, setError } = useBrowserWalletStore();

  const [state, setState] = useState<ImportFormState>({
    method: "mnemonic",
    value: "",
    name: "",
    isImporting: false,
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleImport = async () => {
    // Handle backup method separately
    if (state.method === "backup") {
      if (!selectedFile) {
        setError(t("wallet.import.errors.selectBackupFile"));
        return;
      }
      setState((prev) => ({ ...prev, isImporting: true }));
      try {
        const content = await selectedFile.text();
        await restoreFromBackup(content);
        setModalStep("select"); // Go back to select to authenticate
      } catch {
        setState((prev) => ({ ...prev, isImporting: false }));
      }
      return;
    }

    // Handle mnemonic/privateKey methods
    if (!state.value.trim()) {
      setError(t("wallet.import.errors.enterValue"));
      return;
    }

    setState((prev) => ({ ...prev, isImporting: true }));

    try {
      await importWallet(
        state.method,
        state.value.trim(),
        state.name || undefined,
      );
      closeModal();
    } catch {
      setState((prev) => ({ ...prev, isImporting: false }));
    }
  };

  const isDisabled =
    state.isImporting ||
    connecting ||
    (state.method === "backup" ? !selectedFile : !state.value.trim());

  return (
    <div className="space-y-6 py-4">
      <Button
        variant="ghost"
        size="sm"
        className="gap-2"
        onClick={() => setModalStep("select")}
        disabled={state.isImporting}
      >
        <ArrowLeft className="h-4 w-4" />
        {t("wallet.import.back")}
      </Button>

      <div className="space-y-4">
        {/* Import method selection */}
        <div className="grid grid-cols-3 gap-2">
          <Button
            variant={state.method === "mnemonic" ? "default" : "outline"}
            className="h-auto py-3 flex-col gap-1"
            onClick={() =>
              setState((prev) => ({ ...prev, method: "mnemonic", value: "" }))
            }
            disabled={state.isImporting}
          >
            <FileText className="h-5 w-5" />
            <span className="text-xs">
              {t("wallet.import.methods.mnemonic")}
            </span>
          </Button>
          <Button
            variant={state.method === "privateKey" ? "default" : "outline"}
            className="h-auto py-3 flex-col gap-1"
            onClick={() =>
              setState((prev) => ({ ...prev, method: "privateKey", value: "" }))
            }
            disabled={state.isImporting}
          >
            <Key className="h-5 w-5" />
            <span className="text-xs">
              {t("wallet.import.methods.privateKey")}
            </span>
          </Button>
          <Button
            variant={state.method === "backup" ? "default" : "outline"}
            className="h-auto py-3 flex-col gap-1"
            onClick={() => {
              setState((prev) => ({ ...prev, method: "backup", value: "" }));
              setSelectedFile(null);
            }}
            disabled={state.isImporting}
          >
            <Upload className="h-5 w-5" />
            <span className="text-xs">{t("wallet.import.methods.backup")}</span>
          </Button>
        </div>

        {/* Wallet name - hidden for backup method */}
        {state.method !== "backup" && (
          <div className="space-y-2">
            <Label htmlFor="import-name">{t("wallet.import.nameLabel")}</Label>
            <Input
              id="import-name"
              placeholder={t("wallet.import.namePlaceholder")}
              value={state.name}
              onChange={(e) =>
                setState((prev) => ({ ...prev, name: e.target.value }))
              }
              disabled={state.isImporting}
            />
          </div>
        )}

        {/* Input field */}
        <div className="space-y-2">
          {state.method === "backup" ? (
            <>
              <Label>{t("wallet.import.backupFileLabel")}</Label>
              <div
                className="flex min-h-[120px] w-full items-center justify-center rounded-md border border-dashed border-input bg-background cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() =>
                  document.getElementById("backup-file-input")?.click()
                }
              >
                <input
                  id="backup-file-input"
                  type="file"
                  accept=".json"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setSelectedFile(file);
                      setState((prev) => ({ ...prev, value: file.name }));
                    }
                  }}
                  disabled={state.isImporting}
                />
                {selectedFile ? (
                  <div className="text-center">
                    <FileText className="h-8 w-8 mx-auto mb-2 text-primary" />
                    <p className="text-sm font-medium">{selectedFile.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {t("wallet.import.backupFileChange")}
                    </p>
                  </div>
                ) : (
                  <div className="text-center">
                    <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      {t("wallet.import.backupFileSelect")}
                    </p>
                  </div>
                )}
              </div>
              <p className="text-xs text-warning-dark">
                {t("wallet.import.backupFileWarning")}
              </p>
            </>
          ) : (
            <>
              <Label htmlFor="import-value">
                {state.method === "mnemonic"
                  ? t("wallet.import.mnemonicLabel")
                  : t("wallet.import.privateKeyLabel")}
              </Label>
              {state.method === "mnemonic" ? (
                <textarea
                  id="import-value"
                  className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 font-mono"
                  placeholder={t("wallet.import.mnemonicPlaceholder")}
                  value={state.value}
                  onChange={(e) =>
                    setState((prev) => ({ ...prev, value: e.target.value }))
                  }
                  disabled={state.isImporting}
                />
              ) : (
                <Input
                  id="import-value"
                  type="password"
                  className="font-mono"
                  placeholder={t("wallet.import.privateKeyPlaceholder")}
                  value={state.value}
                  onChange={(e) =>
                    setState((prev) => ({ ...prev, value: e.target.value }))
                  }
                  disabled={state.isImporting}
                />
              )}
              <p className="text-xs text-muted-foreground">
                {state.method === "mnemonic"
                  ? t("wallet.import.mnemonicHint")
                  : t("wallet.import.privateKeyHint")}
              </p>
            </>
          )}
        </div>

        {state.method === "backup" ? (
          <div className="rounded-lg border border-border bg-muted/50 p-4">
            <div className="flex items-start gap-3">
              <Upload className="h-6 w-6 text-primary mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium">
                  {t("wallet.import.backupBoxTitle")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("wallet.import.backupBoxDesc")}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-muted/50 p-4">
            <div className="flex items-start gap-3">
              <Fingerprint className="h-6 w-6 text-primary mt-0.5" />
              <div className="space-y-1">
                <p className="text-sm font-medium">
                  {t("wallet.import.secureBoxTitle")}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t("wallet.import.secureBoxDesc")}
                </p>
              </div>
            </div>
          </div>
        )}

        <Button
          className="w-full"
          size="lg"
          onClick={handleImport}
          disabled={isDisabled}
        >
          {state.isImporting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {state.method === "backup"
                ? t("wallet.import.restoring")
                : t("wallet.import.importing")}
            </>
          ) : state.method === "backup" ? (
            <>
              <Upload className="mr-2 h-4 w-4" />
              {t("wallet.import.submitBackup")}
            </>
          ) : (
            <>
              <Fingerprint className="mr-2 h-4 w-4" />
              {t("wallet.import.submitImport")}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
